import React, { useEffect, useState, useCallback } from "react";
import AnnouncementPopupDialog from "./AnnouncementPopupDialog";
import MarketingNotificationDialog from "./MarketingNotificationDialog";
import { NotificationTypeEnum } from "../modules/shared/model/user-notification.enum";

interface PopupNotification {
  id: string;
  notificationId: string;
  title: string;
  message: string;
  priority?: "low" | "medium" | "high";
  type?: any;
  url?: string;
  expiryTime?: string;
  status?: any;
  createdAt?: string;
  announcementTitle?: string;
  announcementContent?: string;
  popupData?: any;
}

/**
 * PopupNotificationManager Component
 * 
 * This component listens for "showNotificationPopup" custom events
 * and displays AnnouncementPopupDialog modals.
 * 
 * Multiple popups are queued and shown one at a time.
 */
const PopupNotificationManager: React.FC = () => {
  const [popupQueue, setPopupQueue] = useState<PopupNotification[]>([]);
  const [currentPopup, setCurrentPopup] = useState<PopupNotification | null>(null);

  // Listen for showNotificationPopup events
  useEffect(() => {
    const handleShowPopup = (event: CustomEvent<PopupNotification>) => {
      const popupData = event.detail;
      console.log("🎯 [PopupNotificationManager] Received popup request:", popupData);
      
      // Only handle announcement type and marketing type
      const typeId = Number(popupData.type?.id);
      if (typeId !== NotificationTypeEnum.Announcement && typeId !== NotificationTypeEnum.Marketing) {
        console.log("⏭️ [PopupNotificationManager] Skipping non-announcement/marketing type:", typeId);
        return;
      }
      
      let parsedPopupData = { ...popupData };
      
      // Handle announcement type
      if (typeId === NotificationTypeEnum.Announcement) {
        // Use pre-parsed values if available, otherwise parse from message field
        let announcementTitle = popupData.announcementTitle || popupData.title || "New Announcement";
        let announcementContent = popupData.announcementContent || "";
        
        // Only parse JSON if we don't have pre-parsed values
        if (!popupData.announcementTitle && !popupData.announcementContent) {
          try {
            const parsedMessage = JSON.parse(popupData.message || "{}");
            if (parsedMessage.announcementTitle) {
              announcementTitle = parsedMessage.announcementTitle;
            }
            if (parsedMessage.announcementContent) {
              announcementContent = parsedMessage.announcementContent;
            }
          } catch (e) {
            // If not JSON, don't use raw message - keep empty content
            console.log("Failed to parse JSON message:", e);
          }
        }
        
        parsedPopupData = {
          ...popupData,
          announcementTitle,
          announcementContent
        };
        
        console.log("🔍 [PopupNotificationManager] Parsed announcement data:", {
          originalMessage: popupData.message,
          preParsedTitle: popupData.announcementTitle,
          preParsedContent: popupData.announcementContent,
          finalTitle: announcementTitle,
          finalContent: announcementContent,
          finalData: parsedPopupData
        });
      }
      // Handle marketing type
      else if (typeId === NotificationTypeEnum.Marketing) {
        // Parse marketing popup data from message field
        let marketingPopupData = popupData.popupData || {};
        
        try {
          // If popupData is already parsed (from Notification.tsx), use it
          if (popupData.popupData && typeof popupData.popupData === "object") {
            marketingPopupData = popupData.popupData;
            console.log("🎯 [PopupNotificationManager] Using pre-parsed popup data:", marketingPopupData);
          }
          // Otherwise parse from message field (from WebSocket)
          else if (popupData.message) {
            marketingPopupData = JSON.parse(popupData.message || "{}");
            console.log("🎯 [PopupNotificationManager] Parsed popup data from message:", marketingPopupData);
          }
        } catch (e) {
          console.log("Failed to parse marketing popup JSON message:", e);
        }
        
        parsedPopupData = {
          ...popupData,
          popupData: marketingPopupData
        };
        
        console.log("🎯 [PopupNotificationManager] Final parsed popup data:", parsedPopupData);
      }
      
      // Add to queue
      setPopupQueue((prevQueue) => {
        // Check if already in queue to prevent duplicates
        const exists = prevQueue.some(
          (p) => p.id === parsedPopupData.id || p.notificationId === parsedPopupData.notificationId
        );
        
        if (exists) {
          console.log("🚫 [PopupNotificationManager] Popup already in queue:", parsedPopupData.id);
          return prevQueue;
        }
        
        const popupType = typeId === NotificationTypeEnum.Announcement ? "announcement" : "marketing";
        console.log(`✅ [PopupNotificationManager] Added ${popupType} to queue:`, parsedPopupData.id);
        return [...prevQueue, parsedPopupData];
      });
    };

    window.addEventListener("showNotificationPopup", handleShowPopup as EventListener);

    return () => {
      window.removeEventListener("showNotificationPopup", handleShowPopup as EventListener);
    };
  }, []);

  // Process queue - show next popup when current one closes
  useEffect(() => {
    if (!currentPopup && popupQueue.length > 0) {
      const nextPopup = popupQueue[0];
      console.log("📢 [PopupNotificationManager] Showing popup:", nextPopup.id);
      setCurrentPopup(nextPopup);
      setPopupQueue((prevQueue) => prevQueue.slice(1));
    }
  }, [currentPopup, popupQueue]);

  const handleClosePopup = useCallback(() => {
    console.log("🔚 [PopupNotificationManager] Closing popup:", currentPopup?.id);
    setCurrentPopup(null);
  }, [currentPopup]);

  return (
    <>
      {currentPopup && currentPopup.type?.id === NotificationTypeEnum.Announcement && (
        <AnnouncementPopupDialog
          id={currentPopup.id}
          notificationId={currentPopup.notificationId}
          title={currentPopup.title}
          message={currentPopup.message}
          priority={typeof currentPopup.priority === "string" ? currentPopup.priority : "medium"}
          announcementTitle={currentPopup.announcementTitle}
          announcementContent={currentPopup.announcementContent}
          onClose={handleClosePopup}
        />
      )}
      {currentPopup && currentPopup.type?.id === 6 && currentPopup.popupData && (
        <MarketingNotificationDialog
          id={currentPopup.id}
          notificationId={currentPopup.notificationId}
          title={currentPopup.title}
          message={currentPopup.message}
          priority={typeof currentPopup.priority === "string" ? currentPopup.priority : "medium"}
          popupData={currentPopup.popupData}
          onClose={handleClosePopup}
        />
      )}
    </>
  );
};

export default PopupNotificationManager;

